import { useMemo, useState } from 'react';
import { useT, useUiStore } from '@/store';
import { useDefects, useMoveDefect, useAllVehicles, DEFECT_STATUSES } from '@/hooks/useFleet';
import { EmptyState, ErrorState, Panel, SkeletonRows } from '@/components/ui/Panel';
import { API_BASE } from '@/services/api';
import { formatOrderDateTime } from '@/lib/format';
import { DefectFormModal } from './DefectFormModal';

const SEVERITY_TONE = {
  low: 'border-l-dim',
  medium: 'border-l-amber',
  high: 'border-l-red',
  critical_grounded: 'border-l-red',
};

const COLUMN_LABEL = {
  open: 'transportas.defectStatus.open',
  in_repair: 'transportas.defectStatus.in_repair',
  resolved: 'transportas.defectStatus.resolved',
};

/**
 * Defect board — three columns, one card per reported fault.
 *
 * Moves are buttons, not drag-and-drop. Drag targets are a poor fit for the
 * touch screens half these boards are read on, and the move is a two-choice
 * decision (forward, back) rather than free placement — a drag interaction
 * would be more mechanism than the decision needs.
 *
 * A critical_grounded defect is the one severity that side-effects its
 * vehicle on the backend (the truck is taken off the road), so those cards
 * carry the strongest accent and sort to the top of their column.
 */
export function DefectKanban() {
  const t = useT();
  const lang = useUiStore((s) => s.lang);
  const { data: defects = [], isLoading, isError, error } = useDefects();
  const { data: vehicles = [] } = useAllVehicles();
  const moveDefect = useMoveDefect();
  const [reporting, setReporting] = useState(false);

  const plateById = useMemo(
    () => Object.fromEntries(vehicles.map((v) => [v.vehicle_id, v.license_plate])),
    [vehicles],
  );

  const columns = useMemo(() => {
    const grouped = { open: [], in_repair: [], resolved: [] };
    defects.forEach((defect) => {
      (grouped[defect.status] ?? grouped.open).push(defect);
    });
    Object.values(grouped).forEach((list) =>
      list.sort((a, b) => {
        const criticality =
          Number(b.severity === 'critical_grounded') - Number(a.severity === 'critical_grounded');
        if (criticality !== 0) return criticality;
        return String(b.reported_at ?? '').localeCompare(String(a.reported_at ?? ''));
      }),
    );
    return grouped;
  }, [defects]);

  return (
    <Panel
      title={t('transportas.defectsHeading')}
      actions={
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setReporting(true)}
          disabled={vehicles.length === 0}
        >
          {t('transportas.addDefect')}
        </button>
      }
    >
      {isError ? <ErrorState error={error} /> : null}
      {isLoading ? (
        <SkeletonRows rows={4} />
      ) : defects.length === 0 ? (
        <EmptyState>{t('transportas.defectNoneYet')}</EmptyState>
      ) : (
        <div className="grid gap-3 md:grid-cols-3">
          {DEFECT_STATUSES.map((status) => (
            <section key={status} className="rounded-[12px] border border-line bg-white/2 p-2.5">
              <h4 className="mb-2 flex items-center justify-between text-[10.5px] font-bold uppercase tracking-[0.03em] text-dim">
                {t(COLUMN_LABEL[status])}
                <span className="rounded-full bg-white/6 px-2 py-0.5">{columns[status].length}</span>
              </h4>

              <ul className="space-y-2">
                {columns[status].map((defect) => (
                  <li
                    key={defect.defect_id}
                    className={`rounded-[10px] border border-line border-l-[3px] bg-panel-2 p-2.5 ${
                      SEVERITY_TONE[defect.severity] ?? 'border-l-dim'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-[12px] font-semibold">{defect.title}</span>
                      <span className="shrink-0 text-[10px] text-dim">
                        {plateById[defect.vehicle_id] ?? '—'}
                      </span>
                    </div>

                    <div className="mt-0.5 text-[10px] uppercase tracking-[0.03em] text-dim">
                      {t(`transportas.severity.${defect.severity}`)}
                    </div>

                    {defect.description ? (
                      <p className="mt-1.5 text-[11px] leading-snug text-dim">{defect.description}</p>
                    ) : null}

                    {defect.photo_url ? (
                      <a
                        href={`${API_BASE}${defect.photo_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1.5 inline-block text-[10.5px] text-accent"
                      >
                        {t('transportas.defectPhoto')}
                      </a>
                    ) : null}

                    <div className="mt-2 flex items-center justify-between gap-2">
                      <span className="text-[10px] text-dim">
                        {formatOrderDateTime(defect.reported_at, lang)}
                      </span>
                      <div className="flex gap-1">
                        {status !== 'open' ? (
                          <button
                            type="button"
                            className="btn !px-2 !py-0.5 !text-[10px]"
                            onClick={() =>
                              moveDefect.mutate({
                                defectId: defect.defect_id,
                                status: status === 'resolved' ? 'in_repair' : 'open',
                              })
                            }
                          >
                            {t(status === 'resolved' ? 'transportas.moveToInRepair' : 'transportas.moveToOpen')}
                          </button>
                        ) : null}
                        {status !== 'resolved' ? (
                          <button
                            type="button"
                            className="btn btn-primary !px-2 !py-0.5 !text-[10px]"
                            onClick={() =>
                              moveDefect.mutate({
                                defectId: defect.defect_id,
                                status: status === 'open' ? 'in_repair' : 'resolved',
                              })
                            }
                          >
                            {t(status === 'open' ? 'transportas.moveToInRepair' : 'transportas.moveToResolved')}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      {moveDefect.isError ? <ErrorState error={moveDefect.error} /> : null}

      {reporting ? <DefectFormModal onClose={() => setReporting(false)} /> : null}
    </Panel>
  );
}
